import React, { useState, useEffect } from "react";
import ConfirmMessage from "./ConfirmMessage";
import axios from "../../../configApi/axiosConfig";
import { APIURL } from "../../../configApi/apiConfig";
import { SiLinkfire } from "react-icons/si";
import { BsPersonBadge, BsPersonBadgeFill } from "react-icons/bs";
import { PiSignatureLight, PiTimerLight, PiNewspaperBold, PiProhibitLight } from "react-icons/pi";

// ─── Iconos utilitarios ───────────────────────────────────────────────────────

const CheckCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5.5 9l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M9 5.5v4M9 12h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SpinnerIcon = ({ size = 17 }) => (
  <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const FingerprintIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2C8.13 2 5 5.13 5 9v3c0 4.42 3.58 8 8 8s8-3.58 8-8V9c0-3.87-3.13-7-7-7z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 12c0-1.66 1.34-3 3-3s3 1.34 3 3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M12 15v2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="9" r="1" fill="#fff" />
  </svg>
);

const SendIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path d="M8.5 2.5v9M8.5 2.5L5 6M8.5 2.5L12 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 12v.75A2.25 2.25 0 004.75 15h7.5A2.25 2.25 0 0014.5 12.75V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// ─── Configuración de estados (alineada con firmaElectronicaIcons de ListadoSolicitud) ──
// 0=NO APLICA  1=APLICA/PENDIENTE  2=LINK ENVIADO  3=ACEPTADO
// 4=PRUEBA_VIDA_PROCESANDO  5=PRUEBA_VIDA  6=DOCUMENTO  7=RECHAZADO

const FIRMA_ESTADOS = {
  0: { label: "No aplica", badge: "bg-slate-100 text-slate-500" },
  1: { label: "Pendiente", badge: "bg-blue-100 text-blue-700" },
  2: { label: "Link Enviado", badge: "bg-yellow-100 text-yellow-700" },
  3: { label: "Aceptado", badge: "bg-green-100 text-green-700" },
  4: { label: "Procesando", badge: "bg-red-100 text-red-700" },
  5: { label: "Prueba de Vida", badge: "bg-red-100 text-red-700" },
  6: { label: "Completado", badge: "bg-green-100 text-green-700" },
  7: { label: "Rechazado", badge: "bg-red-100 text-red-700" },
  8: { label: "Caducado", badge: "bg-red-100 text-red-700" },
  9: { label: "Yupana OK", badge: "bg-green-100 text-green-700" },
  10: { label: "Yupana Error", badge: "bg-red-100 text-red-700" },
  11: { label: "Yupana Cartera", badge: "bg-blue-100 text-blue-700" },
  12: { label: "Yupana Cartera Error", badge: "bg-red-100 text-red-700" },
  13: { label: "Yupana Pendiente", badge: "bg-yellow-100 text-yellow-700" },
  14: { label: "Yupana Cartera Pendiente", badge: "bg-yellow-100 text-yellow-700" },
  15: { label: "Yupana Cupo Aprobado", badge: "bg-green-100 text-green-700" },
  16: { label: "Yupana Cupo Rechazado", badge: "bg-red-100 text-red-700" },
  17: { label: "Yupana Cupo Espera Aprobación (Fábrica)", badge: "bg-yellow-100 text-yellow-700" },
  18: { label: "Yupana Cupo Espera Aprobación (V. Terrena)", badge: "bg-yellow-100 text-yellow-700" },
};

// ─── Paneles de contenido por estado ─────────────────────────────────────────
// Iconos idénticos a firmaElectronicaIcons en ListadoSolicitud.js

function EstadoLinkEnviado() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
        <SiLinkfire size={30} color="#eab308" />
      </div>
      <div>
        <p className="font-bold text-yellow-800 text-sm">Link ya enviado</p>
        <p className="text-xs text-yellow-700 mt-1 leading-relaxed">
          El enlace biométrico fue enviado al cliente y está pendiente de ser utilizado.
        </p>
      </div>
    </div>
  );
}

function EstadoAceptado() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-green-50 border border-green-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
        <BsPersonBadge size={30} color="#22c55e" />
      </div>
      <div>
        <p className="font-bold text-green-800 text-sm">Firma aceptada</p>
        <p className="text-xs text-green-700 mt-1 leading-relaxed">
          El cliente aceptó el proceso de verificación de identidad exitosamente.
        </p>
      </div>
    </div>
  );
}

function EstadoPruebaVidaProcesando() {
  return (
    <div className="flex flex-col items-center text-center gap-4 bg-red-50 border border-red-200 rounded-xl p-5">
      {/* Icono con anillo giratorio simulando procesamiento */}
      <div className="relative w-14 h-14 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full border-4 border-red-200 border-t-red-500 animate-spin" />
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <BsPersonBadgeFill size={22} color="#dc2626" />
        </div>
      </div>
      <div className="w-full">
        <p className="font-bold text-red-800 text-sm">Procesando prueba de vida</p>
        <p className="text-xs text-red-600 mt-1 leading-relaxed">
          La prueba de vida está siendo analizada. Por favor espere, esto puede tardar unos minutos.
        </p>
        {/* Barra de progreso animada */}
        <div className="mt-3 w-full bg-red-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full bg-red-400 rounded-full animate-pulse"
            style={{ width: '65%' }}
          />
        </div>
        <p className="text-[10px] text-red-400 mt-1.5 animate-pulse tracking-wide">Analizando datos biométricos...</p>
      </div>
    </div>
  );
}

function EstadoPruebaVida() {
  return (
    <div className="flex flex-col items-center text-center gap-4 bg-red-50 border border-red-200 rounded-xl p-5">
      {/* Icono con pulso de onda */}
      <div className="relative w-14 h-14 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-red-200 animate-ping opacity-50" style={{ animationDuration: '1.8s' }} />
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center relative">
          <PiTimerLight size={30} color="#dc2626" />
        </div>
      </div>
      <div className="w-full">
        <p className="font-bold text-red-800 text-sm">Prueba de vida en curso</p>
        <p className="text-xs text-red-600 mt-1 leading-relaxed">
          El cliente está completando la prueba de vida biométrica. Esperando su respuesta.
        </p>
        <p className="text-[10px] text-red-400 mt-2 animate-pulse tracking-wide">Esperando al cliente...</p>
      </div>
    </div>
  );
}

function EstadoDocumento() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-green-50 border border-green-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
        <PiNewspaperBold size={30} color="#16a34a" />
      </div>
      <div>
        <p className="font-bold text-green-800 text-sm">Proceso completado exitosamente</p>
        <p className="text-xs text-green-700 mt-1 leading-relaxed">
          El documento ha sido firmado y todo el proceso biométrico fue completado con éxito.
        </p>
      </div>
    </div>
  );
}

function EstadoRechazado() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-red-50 border border-red-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
        <PiProhibitLight size={32} color="#dc2626" />
      </div>
      <div>
        <p className="font-bold text-red-800 text-sm">Verificación rechazada</p>
        <p className="text-xs text-red-700 mt-1 leading-relaxed">
          El proceso de verificación fue rechazado. Contacte a soporte para más información.
        </p>
      </div>
    </div>
  );
}

function EstadoDesconocido({ idFirmaElectronica }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
        <PiSignatureLight size={30} color="#9ca3af" />
      </div>
      <div>
        <p className="font-bold text-slate-700 text-sm">Estado no reconocido</p>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Estado #{idFirmaElectronica}. Contacte a soporte técnico.
        </p>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Tiempo mínimo que exige Yupana entre el registro del crédito
// (nuevo-credito) y la consulta de estado de cartera (estado-cartera).
const CARTERA_COOLDOWN_MS = 5 * 60 * 1000;

function formatearCooldown(ms) {
  const totalSegundos = Math.ceil(ms / 1000);
  const minutos = Math.floor(totalSegundos / 60);
  const segundos = totalSegundos % 60;
  return `${minutos}:${segundos.toString().padStart(2, "0")}`;
}

function LinkBiometrico({ link, yaExistia }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`rounded-xl border p-4 mt-4 ${yaExistia ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
      <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${yaExistia ? "text-amber-700" : "text-emerald-700"}`}>
        {yaExistia ? "Link generado anteriormente" : "Link biométrico generado"}
      </p>
      <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2">
        <span className="flex-1 text-xs text-slate-700 truncate font-mono">{link}</span>
        <button
          onClick={handleCopy}
          title="Copiar link"
          className="flex-shrink-0 p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
        >
          {copied ? <CheckCircleIcon /> : <CopyIcon />}
        </button>
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          title="Abrir link"
          className="flex-shrink-0 p-1.5 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
        >
          <ExternalLinkIcon />
        </a>
      </div>
    </div>
  );
}

export function ModalFirmaElectronica({ data, isOpen, onClose, isExistPrefactura }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [apiMessage, setApiMessage] = useState(null);
  const [apiMessageType, setApiMessageType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkYaExistia, setLinkYaExistia] = useState(false);
  // Se activa cuando el flujo (crédito + biometría) terminó con éxito, para
  // bloquear la UI y evitar que un doble click o un `data` desactualizado
  // vuelvan a disparar todo el proceso antes de que la pantalla se cierre.
  const [flowCompleted, setFlowCompleted] = useState(false);
  // Confirmación del registro del crédito en Yupana (idFirmaElectronica ===
  // 15: cupo aprobado, o 10: intento anterior fallido). Solo dispara el paso
  // 1 (crédito); cartera y biometría se disparan por separado con
  // handleContinuarProceso, respetando el cooldown de 5 minutos exigido por
  // Yupana entre el registro del crédito y la consulta de cartera.
  const [confirmRegistrarCreditoOpen, setConfirmRegistrarCreditoOpen] = useState(false);
  // Confirmación de continuar el proceso (cartera + biometría) una vez
  // registrado el crédito.
  const [confirmContinuarOpen, setConfirmContinuarOpen] = useState(false);
  // Momento (timestamp en ms) en que se registró el crédito, en base a
  // Cre_SolicitudWebYupanaCredito.FechaSistema (ver getFechaUltimoCreditoExitoso
  // en el backend). Solo se usa para calcular cooldownRestanteMs (el
  // contador de 5 minutos); NO determina qué pantalla se muestra (para eso
  // ver creditoJustRegistradoLocal más abajo) — si determinara la pantalla,
  // cualquier solicitud que alguna vez tuvo un crédito exitoso (incluidas
  // las que ya completaron todo el flujo) quedaría atascada mostrando
  // "Continuar proceso" para siempre.
  const [creditoRegistradoEn, setCreditoRegistradoEn] = useState(null);
  const [cooldownRestanteMs, setCooldownRestanteMs] = useState(0);
  // Se activa únicamente cuando ESTA sesión del modal acaba de registrar el
  // crédito con éxito (handleRegistrarCredito). Sirve para forzar la
  // pantalla "Continuar proceso" (9) mientras `data` (prop del padre) sigue
  // desactualizado en 15/10, igual que `flowCompleted` fuerza el estado 2.
  // A diferencia de creditoRegistradoEn, se resetea al cerrar el modal y NO
  // se recalcula a partir del historial del backend.
  const [creditoJustRegistradoLocal, setCreditoJustRegistradoLocal] = useState(false);

  // Consulta al backend la fecha del último registro de crédito exitoso
  // para este sCre_SolicitudWeb. Devuelve el timestamp en ms o null si no
  // hay ninguno (o si falla la consulta, para no bloquear la UI).
  const obtenerFechaCreditoRegistrado = async () => {
    if (!data?.sCre_SolicitudWeb) return null;
    try {
      const res = await axios.get(APIURL.get_yupana_credito_info(data.sCre_SolicitudWeb));
      const fecha = res?.data?.fechaRegistro;
      return fecha ? new Date(fecha).getTime() : null;
    } catch (e) {
      return null;
    }
  };

  // Estados en los que el cooldown de crédito→cartera es relevante: 9
  // (crédito ok, cartera pendiente) y 12 (cartera falló, puede reintentar
  // tras el cooldown). El resto de estados no muestran ni dependen del
  // contador, así que no vale la pena consultarlo.
  const ESTADOS_CON_COOLDOWN_RELEVANTE = [9, 12];

  useEffect(() => {
    if (!isOpen) {
      setGeneratedLink(null);
      setLinkYaExistia(false);
      setApiMessage(null);
      setApiMessageType(null);
      setFlowCompleted(false);
      setConfirmRegistrarCreditoOpen(false);
      setConfirmContinuarOpen(false);
      setCreditoRegistradoEn(null);
      setCooldownRestanteMs(0);
      setCreditoJustRegistradoLocal(false);
      return;
    }

    if (!ESTADOS_CON_COOLDOWN_RELEVANTE.includes(data?.idFirmaElectronica)) {
      setCreditoRegistradoEn(null);
      return;
    }

    // Al abrir el modal, se retoma el cooldown real (si existe) consultando
    // al backend, para que sobreviva a un cierre/reapertura o recarga.
    let cancelado = false;
    obtenerFechaCreditoRegistrado().then((timestamp) => {
      if (!cancelado) setCreditoRegistradoEn(timestamp);
    });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data?.sCre_SolicitudWeb, data?.idFirmaElectronica]);

  useEffect(() => {
    if (!creditoRegistradoEn) {
      setCooldownRestanteMs(0);
      return;
    }
    const actualizarRestante = () => {
      const restante = CARTERA_COOLDOWN_MS - (Date.now() - creditoRegistradoEn);
      setCooldownRestanteMs(restante > 0 ? restante : 0);
    };
    actualizarRestante();
    const intervalId = setInterval(actualizarRestante, 1000);
    return () => clearInterval(intervalId);
  }, [creditoRegistradoEn]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleConfirm = async () => {
    // Ya se completó el flujo en este ciclo (o el proceso ya no está en un
    // estado que permita reintentar): no se vuelve a ejecutar nada.
    if (isSubmitting || flowCompleted) return;

    setConfirmOpen(false);
    setIsSubmitting(true);
    setApiMessage(null);
    setGeneratedLink(null);
    try {
      // 1. Antes de la biometría, se envía el nuevo crédito a Yupana. Solo se
      // ejecuta cuando idFirmaElectronica está en 1 (pendiente) o 10 (último
      // intento fallido); para cualquier otro estado el crédito ya fue
      // registrado en un intento previo y se salta directo a la biometría
      // (el backend también valida esto de forma autoritativa). Si el envío
      // falla (o Yupana responde con error de negocio), se le informa al
      // cliente y NO se continúa con post_tws_biometria().
      // idFirmaElectronica === 15 (cupo aprobado) ya no pasa por acá: tiene su
      // propio botón/handler (handleRegistrarCredito/handleContinuarProceso)
      // desacoplado de la biometría, con el cooldown de 5 min exigido por
      // Yupana entre el registro del crédito y la consulta de cartera.
      const debeEnviarCredito = data?.idFirmaElectronica === 15;

      if (debeEnviarCredito) {
        const creditoResponse = await axios.post(APIURL.post_yupana_nuevo_credito(), {
          sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
        });
        const creditoData = creditoResponse?.data;
        const creditoOk = !creditoData?.error && creditoData?.message === "OK";

        if (!creditoOk) {
          setApiMessage(
            creditoData?.data?.mensajeRespuesta ||
            creditoData?.message ||
            creditoData?.error ||
            "No fue posible registrar el crédito en Yupana. No se generó el link biométrico."
          );
          setApiMessageType("error");
          return;
        }
      }

      // 2. Crédito registrado correctamente: recién ahí se genera/envía el link biométrico.
      const response = await axios.post(APIURL.post_tws_biometria(), {
        sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
      });
      const resData = response?.data;
      const yaExistia = resData?.yaExistia === true;
      const link = resData?.link ?? null;

      setApiMessage(resData?.message || "El link biométrico fue generado y enviado correctamente.");
      setApiMessageType("success");
      setLinkYaExistia(yaExistia);
      if (link) setGeneratedLink(link);
      // Bloquea la UI (oculta el botón de generar) para que no se vuelva a
      // disparar el proceso mientras el usuario decide cerrar la pantalla.
      setFlowCompleted(true);
    } catch (err) {
      setApiMessage(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "No fue posible generar el link biométrico. Intente nuevamente."
      );
      setApiMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Paso 1 de la cadena: registrar el crédito en Yupana. Se dispara desde
  // idFirmaElectronica === 15 (cupo aprobado) o 10 (intento anterior
  // fallido). NO continúa con cartera/biometría: Yupana exige esperar 5
  // minutos desde el registro del crédito antes de poder consultar cartera,
  // así que solo arranca el cooldown; el usuario retoma manualmente con
  // handleContinuarProceso una vez que el botón se habilite.
  const handleRegistrarCredito = async () => {
    if (isSubmitting || flowCompleted) return;

    setConfirmRegistrarCreditoOpen(false);
    setIsSubmitting(true);
    setApiMessage(null);
    setGeneratedLink(null);
    try {
      const creditoResponse = await axios.post(APIURL.post_yupana_nuevo_credito(), {
        sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
      });
      const creditoData = creditoResponse?.data;
      const creditoOk = !creditoData?.error && creditoData?.message === "OK";

      if (!creditoOk) {
        setApiMessage(
          creditoData?.data?.mensajeRespuesta ||
          creditoData?.message ||
          creditoData?.error ||
          "No fue posible registrar el crédito en Yupana."
        );
        setApiMessageType("error");
        return;
      }

      // Se relee la fecha desde el backend (Cre_SolicitudWebYupanaCredito)
      // en vez de usar la hora local, para que el cooldown quede anclado al
      // reloj del servidor y sobreviva a un cierre/reapertura del modal.
      const timestamp = await obtenerFechaCreditoRegistrado();
      setCreditoRegistradoEn(timestamp ?? Date.now());
      // Fuerza la pantalla "Continuar proceso" (9) en esta sesión, ya que
      // `data` (prop del padre) sigue mostrando el estado previo (15/10).
      setCreditoJustRegistradoLocal(true);
      setApiMessage(
        "Crédito registrado correctamente en Yupana. Debe esperar 5 minutos antes de continuar con la consulta de cartera."
      );
      setApiMessageType("success");
    } catch (err) {
      setApiMessage(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "No fue posible registrar el crédito en Yupana. Intente nuevamente."
      );
      setApiMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Paso 2 (consultar cartera, salvo que ya se haya resuelto con éxito) +
  // paso 3 (generar/enviar el link biométrico). Se dispara desde
  // idFirmaElectronica === 9 (crédito ok), 11 (cartera ok) o 12 (cartera con
  // error). Bloqueado mientras cooldownRestanteMs > 0 (cooldown de 5 min
  // tras registrar el crédito, ver handleRegistrarCredito).
  const handleContinuarProceso = async () => {
    if (isSubmitting || flowCompleted || cooldownRestanteMs > 0) return;

    // La consulta de cartera no tiene guarda de idempotencia en el backend
    // (a diferencia de crédito y biometría): siempre vuelve a llamar a
    // Yupana si se invoca. Por eso, si al entrar aquí la cartera ya se
    // consultó con éxito (idFirmaElectronica === 11), se omite el paso 2
    // para no volver a consumir ese servicio y se retoma directo en biometría.
    const carteraYaConsultada = data?.idFirmaElectronica === 11;

    setConfirmContinuarOpen(false);
    setIsSubmitting(true);
    setApiMessage(null);
    setGeneratedLink(null);
    try {
      if (!carteraYaConsultada) {
        const carteraResponse = await axios.post(APIURL.post_yupana_estado_cartera(), {
          sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
        });
        const carteraData = carteraResponse?.data;
        const carteraOk = !carteraData?.error && carteraData?.message === "OK";

        if (!carteraOk) {
          setApiMessage(
            carteraData?.message ||
            carteraData?.error ||
            "No fue posible consultar el estado de cartera en Yupana."
          );
          setApiMessageType("error");
          return;
        }
      }

      const response = await axios.post(APIURL.post_tws_biometria(), {
        sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
      });
      const resData = response?.data;
      const yaExistia = resData?.yaExistia === true;
      const link = resData?.link ?? null;

      setApiMessage(resData?.message || "El link biométrico fue generado y enviado correctamente.");
      setApiMessageType("success");
      setLinkYaExistia(yaExistia);
      if (link) setGeneratedLink(link);
      setFlowCompleted(true);
    } catch (err) {
      setApiMessage(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "No fue posible completar el proceso con Yupana. Intente nuevamente."
      );
      setApiMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const firmaId = data?.idFirmaElectronica;
  // Una vez que el flujo terminó con éxito en este ciclo, se fuerza el
  // estado "Link Enviado" para ocultar el botón de generar y así evitar que
  // se vuelva a disparar el proceso (crédito + biometría) hasta que la
  // pantalla se cierre y `data` se refresque con el estado real del backend.
  // Del mismo modo, una vez registrado el crédito en esta sesión se fuerza
  // el estado "Crédito registrado" (9) aunque `data` siga desactualizado en
  // 15 o 10, para mostrar el cooldown/botón de continuar correctos. Se usa
  // creditoJustRegistradoLocal (no creditoRegistradoEn) porque este último
  // se recalcula desde el historial del backend y existiría para cualquier
  // solicitud que alguna vez tuvo un crédito exitoso, incluidas las que ya
  // avanzaron más allá del paso 9 (cartera, biometría, etc.).
  const firmaIdEfectivo = flowCompleted ? 2 : (creditoJustRegistradoLocal ? 9 : firmaId);
  const estadoInfo = FIRMA_ESTADOS[firmaIdEfectivo];

  const renderContenido = () => {
    switch (firmaIdEfectivo) {
      case 1:
        return (
          <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiSignatureLight size={16} color="#1D4ED8" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-700">
                Link biométrico disponible
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Envío del enlace de verificación
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              Se generará y enviará un enlace biométrico al cliente para verificar su identidad
              y completar la firma digital. El titular debe tener su{" "}
              <strong className="text-slate-800">cédula física disponible</strong>.
            </p>
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                  ? "bg-blue-600/70 text-white cursor-not-allowed"
                  : "bg-blue-700 text-white hover:bg-blue-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                }`}
              onClick={() => setConfirmOpen(true)}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Generando enlace...</span></>
              ) : (
                <><SendIcon /><span>Generar link biométrico</span></>
              )}
            </button>
          </div>
        );
      case 2: return <EstadoLinkEnviado />;
      case 3: return <EstadoAceptado />;
      case 4: return <EstadoPruebaVidaProcesando />;
      case 5: return <EstadoPruebaVida />;
      case 6: return <EstadoDocumento />;
      case 9: {
        const enCooldown = cooldownRestanteMs > 0;
        return (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiSignatureLight size={16} color="#047857" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                Crédito registrado en Yupana
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Continuar con la verificación
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              {enCooldown
                ? "El crédito ya fue registrado en Yupana. Por requerimiento del proveedor, debe esperar 5 minutos antes de consultar el estado de cartera."
                : "El crédito ya fue registrado en Yupana. Falta consultar el estado de cartera y generar el link biométrico para continuar."}
            </p>
            {enCooldown ? (
              <div className="w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm bg-emerald-100 text-emerald-700 border border-emerald-200">
                <PiTimerLight size={18} aria-hidden="true" />
                <span>Podrá continuar en {formatearCooldown(cooldownRestanteMs)}</span>
              </div>
            ) : (
              <button
                className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                    ? "bg-emerald-600/70 text-white cursor-not-allowed"
                    : "bg-emerald-700 text-white hover:bg-emerald-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                  }`}
                onClick={() => setConfirmContinuarOpen(true)}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <><SpinnerIcon /><span>Procesando...</span></>
                ) : (
                  <><SendIcon /><span>Continuar proceso</span></>
                )}
              </button>
            )}
          </div>
        );
      }
      case 10:
        return (
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiProhibitLight size={16} color="#dc2626" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700">
                Error al registrar crédito en Yupana
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Reintentar registro de crédito
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              El intento anterior de registrar el crédito en Yupana{" "}
              <strong className="text-red-700">falló</strong>. Puede reintentarlo; luego
              deberá esperar 5 minutos antes de continuar con el proceso.
            </p>
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                  ? "bg-red-600/70 text-white cursor-not-allowed"
                  : "bg-red-700 text-white hover:bg-red-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                }`}
              onClick={() => setConfirmRegistrarCreditoOpen(true)}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Registrando crédito...</span></>
              ) : (
                <><SendIcon /><span>Reintentar registro de crédito</span></>
              )}
            </button>
          </div>
        );
      case 15:
        return (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiSignatureLight size={16} color="#047857" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                Cupo aprobado por Yupana
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Registrar información de crédito
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              El cupo del cliente fue aprobado. Registre la información del crédito en Yupana
              para continuar con el proceso.
            </p>
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                  ? "bg-emerald-600/70 text-white cursor-not-allowed"
                  : "bg-emerald-700 text-white hover:bg-emerald-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                }`}
              onClick={() => setConfirmRegistrarCreditoOpen(true)}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Registrando crédito...</span></>
              ) : (
                <><SendIcon /><span>Registrar crédito en Yupana</span></>
              )}
            </button>
          </div>
        );
      case 11:
        return (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiSignatureLight size={16} color="#047857" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                Cartera consultada en Yupana
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Continuar con la verificación
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              El crédito y la cartera ya fueron registrados/consultados en Yupana. Falta
              generar el link biométrico para continuar.
            </p>
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                  ? "bg-emerald-600/70 text-white cursor-not-allowed"
                  : "bg-emerald-700 text-white hover:bg-emerald-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                }`}
              onClick={() => setConfirmContinuarOpen(true)}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Procesando...</span></>
              ) : (
                <><SendIcon /><span>Continuar proceso</span></>
              )}
            </button>
          </div>
        );
      case 12:
        return (
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiProhibitLight size={16} color="#dc2626" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700">
                Error al consultar cartera en Yupana
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Reintentar proceso
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              El crédito ya fue registrado en Yupana, pero la consulta de{" "}
              <strong className="text-red-700">estado de cartera falló</strong>. Puede
              reintentar la consulta de cartera para, si sale bien, generar el link biométrico.
            </p>
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                  ? "bg-red-600/70 text-white cursor-not-allowed"
                  : "bg-red-700 text-white hover:bg-red-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                }`}
              onClick={() => setConfirmContinuarOpen(true)}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Reintentando...</span></>
              ) : (
                <><SendIcon /><span>Reintentar proceso</span></>
              )}
            </button>
          </div>
        );
      case 7:
        return (
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiProhibitLight size={16} color="#dc2626" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700">
                Verificación rechazada / caducada
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Reenviar enlace de verificación
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              El proceso anterior fue <strong className="text-red-700">rechazado o caducó</strong>.
              Puede generar un nuevo enlace biométrico para que el cliente complete la verificación nuevamente.
            </p>
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${isSubmitting
                  ? "bg-red-600/70 text-white cursor-not-allowed"
                  : "bg-red-700 text-white hover:bg-red-800 shadow-md hover:shadow-lg active:scale-[0.98]"
                }`}
              onClick={() => setConfirmOpen(true)}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Generando enlace...</span></>
              ) : (
                <><SendIcon /><span>Generar nuevo link biométrico</span></>
              )}
            </button>
          </div>
        );
      default: return <EstadoDesconocido idFirmaElectronica={firmaId} />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="firma-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center flex-shrink-0">
              <FingerprintIcon />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-blue-200/80 font-medium">
                Verificación de identidad
              </p>
              <h2 id="firma-modal-title" className="text-base font-bold text-white leading-tight truncate">
                Firma Electrónica
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0 ml-3"
            aria-label="Cerrar modal"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Titular + badge de estado */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-0.5">
                Titular
              </p>
              <p className="text-sm font-bold text-slate-800 leading-snug truncate">
                {data?.nombre || "—"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                C.I. <span className="font-semibold text-slate-700">{data?.cedula || "—"}</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs text-slate-600">
                <span className="text-slate-400 font-normal">Nro.</span>
                <span className="font-bold text-slate-800">#{data?.NumeroSolicitud || "—"}</span>
              </span>
              {estadoInfo && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${estadoInfo.badge}`}>
                  {estadoInfo.label}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Feedback API */}
        {apiMessage && (
          <div className="mx-6 mt-5">
            <div
              role="alert"
              aria-live="polite"
              className={`flex items-start gap-3 rounded-xl px-4 py-3.5 text-sm leading-snug border ${apiMessageType === "success"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-rose-50 border-rose-200 text-rose-800"
                }`}
            >
              <span className="flex-shrink-0 mt-0.5">
                {apiMessageType === "success" ? <CheckCircleIcon /> : <AlertCircleIcon />}
              </span>
              <p className="flex-1 font-medium">{apiMessage}</p>
              <button
                onClick={() => { setApiMessage(null); setApiMessageType(null); setGeneratedLink(null); }}
                className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                aria-label="Cerrar mensaje"
              >
                <CloseIcon />
              </button>
            </div>
            {/* Link biométrico generado */}
            {generatedLink && (
              <LinkBiometrico link={generatedLink} yaExistia={linkYaExistia} />
            )}
          </div>
        )}

        {/* Contenido según estado — scrollable si el contenido es largo */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {renderContenido()}
        </div>
      </div>

      <ConfirmMessage
        isOpen={confirmOpen}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
        message="¿Está seguro que desea generar el link biométrico? Recuerde que el cliente debe contar con su cédula física."
      />

      <ConfirmMessage
        isOpen={confirmRegistrarCreditoOpen}
        onConfirm={handleRegistrarCredito}
        onCancel={() => setConfirmRegistrarCreditoOpen(false)}
        message="¿Está seguro que desea registrar la información del crédito en Yupana? Luego de registrarlo deberá esperar 5 minutos antes de continuar con el proceso."
      />

      <ConfirmMessage
        isOpen={confirmContinuarOpen}
        onConfirm={handleContinuarProceso}
        onCancel={() => setConfirmContinuarOpen(false)}
        message="¿Está seguro que desea continuar con el proceso (consulta de cartera y generación del link biométrico)?"
      />
    </div>
  );
}
